import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Injector,
    Input,
    ViewChild
} from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import {
    ConnectedPosition,
    Overlay,
    OverlayRef
} from "@angular/cdk/overlay";
import { CdkPortal } from "@angular/cdk/portal";

import { SelectionListItem } from "../../models/selection-list-item.model";
import { BaseControlValueAccessor, MakeValueAccessorProvider } from "../base-control-value-accessor.component";
import { ValidationService } from "../validation-messages/validation.service";
import { DeviceDetectorService } from "ngx-device-detector";
import { DateAdapter } from "@angular/material/core";

export interface SelectOverlayItem {
    extraLabelText: string;
    isDisabled: boolean;
    text: string;
    value: string | number | boolean;
}

@Component({
    selector: "ha-select-overlay",
    templateUrl: "./select-overlay.component.html",
    styleUrls: ["./select-overlay.component.scss"],
    providers: [MakeValueAccessorProvider(SelectOverlayComponent)],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectOverlayComponent extends BaseControlValueAccessor<string> {
    @Input()
    public label: string;

    @Input()
    public set options(value: SelectionListItem[] | null) {
        this.listItems = this.genereateListItems(value);
    }

    @Input()
    public sortWhenTranslated: boolean;

    @ViewChild("trigger", { read: ElementRef })
    public trigger: ElementRef<HTMLElement>;

    @ViewChild(CdkPortal)
    public portal: CdkPortal;

    public listItems: SelectOverlayItem[] = [];
    public expanded = false;

    private overlayRef: OverlayRef | null = null;

    constructor(
        injector: Injector,
        validationService: ValidationService,
        changeDetectorRef: ChangeDetectorRef,
        deviceDetectorService: DeviceDetectorService,
        translocoService: TranslocoService,
        dateAdapter: DateAdapter<unknown>,
        private overlay: Overlay
    ) {
        super(
            injector,
            validationService,
            changeDetectorRef,
            deviceDetectorService,
            translocoService,
            dateAdapter
        );
    }

    public get isUnselected() {
        return this.value === undefined && this.listItems[0]?.value !== undefined;
    }

    public genereateListItems(options: SelectionListItem[] | null): SelectOverlayItem[] {
        const translocoService = this.injector.get(TranslocoService);

        const listItems = options?.filter(x => !x.isDisabled).map((item) => {
            return <SelectOverlayItem>{
                extraLabelText: item.extraLabelLangKey != null
                    ? translocoService.translate(item.extraLabelLangKey)
                    : item.extraLabelText,
                isDisabled: item.isDisabled,
                text: item.langKey != null ? translocoService.translate(item.langKey) : item.text,
                value: item.value,
            };
        }) ?? [];

        return this.sortWhenTranslated
            ? listItems.sort((a, b) => {
                if (!b.value) {
                    return 1;
                }

                return a.text < b.text ? -1 : 1;
            })
            : listItems;
    }

    public get text(): string {
        return this.listItems?.find((_) => _.value === this.value)?.text;
    }

    public get extraLabelText(): string {
        return this.listItems?.find((_) => _.value == this.value)?.extraLabelText;
    }

    public toggleExpand(): void {
        if (this.expanded) {
            this.close();
        } else {
            this.open();
        }
    }

    public selectOption(option: SelectOverlayItem, event?: Event): void {
        event?.stopPropagation();

        if (option.isDisabled) {
            return;
        }

        this.value = option.value as string;
        this.close();
        this.changeDetectorRef.markForCheck();
    }

    public close(): void {
        this.expanded = false;

        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    private open(): void {
        if (this.expanded || !this.trigger?.nativeElement || !this.portal) {
            return;
        }

        const positions: ConnectedPosition[] = [
            {
                originX: "start",
                originY: "bottom",
                overlayX: "start",
                overlayY: "top",
            },
            {
                originX: "start",
                originY: "top",
                overlayX: "start",
                overlayY: "bottom",
            }
        ];

        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.trigger.nativeElement)
            .withPositions(positions)
            .withFlexibleDimensions(false)
            .withPush(false);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            width: this.trigger.nativeElement.getBoundingClientRect().width,
            panelClass: "ha-select-overlay-panel"
        });

        this.overlayRef.attach(this.portal);
        this.expanded = true;
        this.changeDetectorRef.markForCheck();
    }
}
